/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import {useEffect, useState} from 'react'
import clsx from 'clsx'
import {Controller, useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {toast} from 'utils/toastUtils'
import CompanyFormInput from 'pages/Front/Booking/components/CompanyForm/CompanyFormInput'
import UiFormInner from 'components/Forms/UIFormInner'
import UiFormGrid from 'components/Forms/UiFormGrid'
import UiFormField from 'components/Forms/UiFormField'
import {zodResolver} from '@hookform/resolvers/zod'
import {LeadTaskInputs, useLeadTaskSchema} from 'components/leads/schemas/useLeadTaskSchema'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import UiDatePicker from 'components/Ui/UiDatePicker'
import UiFormSelectField from 'components/Forms/UiFormSelectField'
import Dates from 'utils/Dates'
import {LeadTaskType} from 'types/enums/LeadTaskType'
import {LeadTaskStatus} from 'types/enums/LeadTaskStatus'
import {LeadTask} from 'types/entities/LeadTask'
import type {Lead} from 'types/entities/Lead'
import {closeSecondaryModal} from 'helpers/actions'
import {useAppDispatch} from 'helpers/store'
import {useLeadContext} from 'utils/hooks/useLeadContext'
import {useSellers} from 'utils/sellerUtils'

interface LeadTaskFormProps {
    lead: Lead
    task?: LeadTask
    onChange: (task: LeadTask) => void
}
const LeadTaskForm = (props: LeadTaskFormProps) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const { actions: action } = useLeadContext(props.lead.category)
    const { selectOptions: sellerOptions } = useSellers()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [workingTaskId, setWorkingTaskId] = useState<number | undefined>(props.task?.id)
    const [titleManuallyEdited, setTitleManuallyEdited] = useState(!!props.task?.title)
	const defaultDueDate = new Date(
        new Date().getTime() + 24 * 60 * 60 * 1000 - new Date().getMinutes() * 60 * 1000
    )
    const LeadTaskSchema = useLeadTaskSchema()
    const {
        register,
        handleSubmit,
        control,
        watch,
        setValue,
        formState: { errors },
    } = useForm<LeadTaskInputs>({
        defaultValues: {
            seller_id: props.lead.seller_id,
            type: props.task?.type || LeadTaskType.ContactAttempt,
            status: props.task?.status || LeadTaskStatus.Open,
            title: props.task?.title || '',
            description: props.task?.description || '',
            due_at: props.task?.due_at ? new Date(props.task.due_at) :defaultDueDate,
        },
        resolver: zodResolver(LeadTaskSchema),
    })

    const typeOptions = [
        { label: t('Kontaktaufnahme'), value: LeadTaskType.ContactAttempt },
        { label: t('Follow-up'), value: LeadTaskType.FollowUp },
        { label: t('Telefon'), value: LeadTaskType.Call },
        { label: t('Mail'), value: LeadTaskType.Email },
        { label: t('Sonstiges'), value: LeadTaskType.Other },
    ]

    const statusOptions = [
        { label: t('Offen'), value: LeadTaskStatus.Open },
        { label: t('Verschoben'), value: LeadTaskStatus.Postponed },
        { label: t('Erledigt'), value: LeadTaskStatus.Done },
    ]

    const leadObjectName =
        props.lead.company_name?.trim() ||
        props.lead.contact_person?.trim() ||
        props.lead.full_name?.trim() ||
        String(props.lead.id)

    const currentType = watch('type')
    const currentTypeLabel = typeOptions.find((option) => option.value === currentType)?.label ?? currentType

    // Auto-generate title from [Task-Typ] – [Objektname] when type changes and user hasn't manually edited
    useEffect(() => {
        if (titleManuallyEdited) return
        setValue('title', `${currentTypeLabel} – ${leadObjectName}`)
    }, [currentTypeLabel, leadObjectName, setValue, titleManuallyEdited])

    const onSubmit = async (data: LeadTaskInputs, shouldClose: boolean = true) => {
        setIsSubmitting(true)
        try {
            const payload = {
                id: workingTaskId,
                lead_id: props.lead.id,
                seller_id: data.seller_id,
                title: `${data.title}`.trim(),
                description: `${data.description ?? ''}`.trim(),
                type: data.type ?? LeadTaskType.ContactAttempt,
                status: data.status ?? LeadTaskStatus.Open,
                due_at: Dates.dayjs(data.due_at).format('YYYY-MM-DD HH:mm:ss'),
            } as unknown as Partial<LeadTask>
            const response = await dispatch(action.fetchCreateLeadTask(payload))
            toast.success(t('Task saved'))
            if (response?.task) {
                setWorkingTaskId(response.task.id)
                props.onChange(response.task)
            }
            if (shouldClose) {
                dispatch(closeSecondaryModal())
            }
        } catch {
            toast.error(t('Failed to save task'))
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <form
            onSubmit={handleSubmit((data) => onSubmit(data, true))}
            className={clsx('LeadTaskForm flex flex-col grow')}
        >
            <UiFormInner>
                <div className="px-1">
                    <UiFormGrid cols={12} gap={4} responsive={false} className={clsx('grid-cols-12 w-full min-w-0')}>
                        <Controller
                            control={control}
                            name="type"
                            render={({ field, fieldState }) => (
                                <UiFormSelectField
                                    className={clsx('col-span-12 sm:col-span-4')}
                                    label={t('Task-Typ')}
                                    options={typeOptions}
                                    name={field.name}
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={fieldState.error?.message}
                                />
                            )}
                        />

                        <Controller
                            control={control}
                            name="status"
                            render={({ field, fieldState }) => (
                                <UiFormSelectField
                                    className={clsx('col-span-12 sm:col-span-4')}
                                    label={t('Status')}
                                    options={statusOptions}
                                    name={field.name}
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={fieldState.error?.message}
                                />
                            )}
                        />

						<UiFormField
							className={clsx('col-span-12 sm:col-span-4')}
							label={t('Due Date & Time')}
							error={errors.due_at?.message}
						>
							<Controller
								control={control}
								name="due_at"
								render={({ field }) => (
									<UiDatePicker
										value={field.value ? new Date(field.value) : null}
										includeTime={true}
										onChange={(date) => {
											field.onChange(date ?? null)
										}}
									/>
								)}
							/>
						</UiFormField>

                        <Controller
                            control={control}
                            name="seller_id"
                            render={({ field, fieldState }) => (
                                <UiFormSelectField
                                    className={clsx('col-span-12')}
                                    label={t('Responsible')}
                                    options={sellerOptions.map((o) => ({ label: String(o.label), value: o.value }))}
                                    name={field.name}
                                    value={field.value}
                                    onChange={(val) => field.onChange(Number(val))}
                                    error={fieldState.error?.message}
                                />
                            )}
                        />

                        <CompanyFormInput
                            className={clsx('col-span-12')}
                            label={t('Title')}
                            name="title"
                            register={register}
                            errors={errors}
							required={true}
                            placeholder={t('What needs to be done?')}
                            registerOptions={{ onChange: () => setTitleManuallyEdited(true) }}
                        />

                        <CompanyFormInput
                            className={clsx('col-span-12')}
                            label={t('Description')}
                            name="description"
                            component="textarea"
                            register={register}
                            errors={errors}
                            placeholder={t('Details...')}
                        />
                    </UiFormGrid>
                </div>
            </UiFormInner>

            <UiFormActionsBar
                primaryText={isSubmitting ? t('Saving task...') : t('Save')}
                onPrimary={handleSubmit((data) => onSubmit(data, false))}
                saveAndCloseText={isSubmitting ? t('Saving task...') : t('Save & Close')}
                onSaveAndClose={handleSubmit((data) => onSubmit(data, true))}
                loading={isSubmitting}
            />
        </form>
    )
}

export default LeadTaskForm

